import React from 'react';

import { CSSTransition } from 'react-transition-group';
import { ExpandableContentContent, ExpandableContentWrapper } from './ExpandableContent.styles';
import { AnimationType, ExpandableContentProps } from './ExpandableContent.types';

const ANIMATION_DURATION = 400;

interface ExpandableFunctions {
  onEnter: (node: HTMLElement) => void | undefined;
  onEntering: (node: HTMLElement) => void | undefined;
  onEntered: (node: HTMLElement) => void | undefined;
  onExit: (node: HTMLElement) => void | undefined;
  onExiting: (node: HTMLElement) => void | undefined;
  onExited: (node: HTMLElement) => void | undefined;
}

const setOpacityToZero = (node: HTMLElement) => {
  node.style.opacity = '0';
};

const setOpacityToOne = (node: HTMLElement) => {
  node.style.opacity = '1';
};

const setNodeHeightToZero = (node: HTMLElement) => {
  node.style.height = '0px';
  node.style.opacity = '0';
};

const setNodeHeightBasedOnFirstChild = (node: HTMLElement) => {
  const wrapper = node.firstElementChild as HTMLElement;
  node.style.height = `${wrapper.scrollHeight}px`;
  node.style.opacity = '1';
};

const cssTransitionOpacityFunctions = {
  onEnter: setOpacityToZero,
  onEntering: setOpacityToOne,
  onExit: setOpacityToOne,
  onExiting: setOpacityToZero,
} as ExpandableFunctions;

const cssTransitionHeightFunctions = {
  onEnter: setNodeHeightToZero,
  onEntering: setNodeHeightBasedOnFirstChild,
  onExit: setNodeHeightBasedOnFirstChild,
  onExiting: setNodeHeightToZero,
} as ExpandableFunctions;

const getAnimationFunctions = (type: AnimationType | undefined): ExpandableFunctions => {
  if (type === AnimationType.Opacity) {
    return cssTransitionOpacityFunctions;
  }
  return cssTransitionHeightFunctions;
};

export default function ExpandableContent({ animationDuration, expanded, children, type }: ExpandableContentProps) {
  const duration = animationDuration || ANIMATION_DURATION;
  const animationFunctions = getAnimationFunctions(type);

  return (
    <CSSTransition
      mountOnEnter={false}
      unmountOnExit
      onEnter={animationFunctions.onEnter}
      onEntering={animationFunctions.onEntering}
      onExit={animationFunctions.onExit}
      onExiting={animationFunctions.onExiting}
      classNames="details"
      timeout={duration}
      in={expanded}
    >
      <ExpandableContentWrapper animationDuration={duration}>
        <ExpandableContentContent>{children}</ExpandableContentContent>
      </ExpandableContentWrapper>
    </CSSTransition>
  );
}
